import React, { useState, useEffect } from 'react';
import { db, collection, getDocs, doc, setDoc } from '../firebase';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { leagueToolsTranslations } from '../i18n/translations';




const MatchScore = ({ match, onScoreSave, onScoreChange, isSaving, t }) => {
  const handleSetScoreChange = (setIndex, teamKey, score) => {
    onScoreChange(match.id, setIndex, teamKey, score);
  };

  const handleSave = () => {
    onScoreSave(match);
  };

  // 🔢 Calcul ELO de chaque équipe
  const eloTeamA = (match.joueur1A?.elo || 0) + (match.joueur2A?.elo || 0);
  const eloTeamB = (match.joueur1B?.elo || 0) + (match.joueur2B?.elo || 0);

  return (
    <div className="bg-white p-6 rounded-lg shadow-md border mb-6 hover:shadow-lg transition-shadow text-center">
      <div className="text-center mb-4">
  <div className="flex flex-col items-center gap-1">
    <h3 className="text-xl font-semibold text-[#800020]">
      {match.teamA}{' '}
      <span className="text-sm text-gray-500 font-medium">
        ({eloTeamA} {t('pointsShort')})
      </span>
    </h3>
    <span className="text-gray-500 text-lg">VS</span>
    <h3 className="text-xl font-semibold text-[#800020]">
      {match.teamB}{' '}
      <span className="text-sm text-gray-500 font-medium">
        ({eloTeamB} {t('pointsShort')})
      </span>
    </h3>
  </div>
</div>


      {match.sets.map((set, index) => (
        <div className="flex justify-between items-center gap-4">
        <div className="flex-1 text-center">
          <label className="text-sm text-gray-600 block break-words">{t('setScore', { number: index + 1 })}</label>
          <input
  type="number"
  value={set.teamAScore ?? 0}
  onChange={(e) =>
    !match.locked &&
    handleSetScoreChange(index, 'teamAScore', parseInt(e.target.value, 10) || 0)
  }
  className={`p-2 border rounded-lg w-20 text-center focus:ring-2 focus:ring-[#800020] focus:outline-none ${
    match.locked ? 'bg-gray-200 cursor-not-allowed' : ''
  }`}
  disabled={match.locked} // Désactiver si le match est verrouillé
/>

<input
  type="number"
  value={set.teamBScore ?? 0}
  onChange={(e) =>
    !match.locked &&
    handleSetScoreChange(index, 'teamBScore', parseInt(e.target.value, 10) || 0)
  }
  className={`p-2 border rounded-lg w-20 text-center focus:ring-2 focus:ring-[#800020] focus:outline-none ${
    match.locked ? 'bg-gray-200 cursor-not-allowed' : ''
  }`}
  disabled={match.locked} // Désactiver si le match est verrouillé
/>

        </div>
      </div>
      
      ))}
      <div className="text-center">
      <button
  onClick={handleSave}
  className={`px-6 py-2 bg-gradient-to-r from-[#800020] to-[#ea4c89] text-white rounded-full shadow-md hover:shadow-xl hover:scale-105 transition-transform ${
    match.locked ? 'bg-gray-400 cursor-not-allowed' : ''
  }`}
  disabled={isSaving || match.locked} // Désactiver si le match est verrouillé
>
  {isSaving ? t('saving') : t('saveScore')}
</button>

        {isSaving && <div className="text-sm text-green-600 mt-2">{t('scoreSaved')}</div>}
      </div>
    </div>
  );
};

const EncodeScoresLeague = () => {
  const { language } = useLanguage();
  const t = createTranslator(language, leagueToolsTranslations);
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leagueId, setLeagueId] = useState(null);
  const [savingMatchId, setSavingMatchId] = useState(null);
  const [groupedMatches, setGroupedMatches] = useState([]);
  const [collapsedWeeks, setCollapsedWeeks] = useState([]);
  const [eloByPhone, setEloByPhone] = useState({});

  

  const navigate = useNavigate();
  const toggleWeekCollapse = (weekIndex) => {
    setCollapsedWeeks((prev) => {
      const newCollapsedWeeks = [...prev];
      newCollapsedWeeks[weekIndex] = !newCollapsedWeeks[weekIndex];
      return newCollapsedWeeks;
    });
  };
  


  const handleDownloadPDF = (weekMatches, weekNumber) => {
    const doc = new jsPDF();
  
    // Ajouter un beau titre
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(`Score des équipes - Semaine ${weekNumber} / ${groupedMatches.length}`, 10, 20);
  
    // Ajout d'un soulignement pour le titre
    const textWidth = doc.getTextWidth(`Score des équipes - Semaine ${weekNumber}`);
    doc.setDrawColor(128, 0, 32); // Rouge sombre
    doc.setLineWidth(1);
    doc.line(10, 22, 10 + textWidth, 22);
  
    // Ajouter le logo en haut à droite
    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
      });
    };
  
    const addLogo = async () => {
      try {
        const image = await loadImage('/logo2.jpg'); // Remplacez par le chemin de votre logo
        doc.addImage(image, 'JPEG', 160, 10, 30, 30); // Ajuste la taille et la position
      } catch (error) {
        console.error('Erreur lors du chargement du logo :', error);
      }
    };
  
    addLogo().then(() => {
      // Préparer les données pour le tableau
      const tableData = weekMatches.map((match) => [
        match.teamA,
        match.teamB,
        match.sets[0]?.teamAScore || 0,
        match.sets[0]?.teamBScore || 0,
        match.sets[1]?.teamAScore || 0,
        match.sets[1]?.teamBScore || 0,
        match.sets[2]?.teamAScore || 0,
        match.sets[2]?.teamBScore || 0,
      ]);
  
      // Ajouter le tableau avec le head en rouge
      doc.autoTable({
        head: [
          [
            'Équipe A',
            'Équipe B',
            'Set 1A',
            'Set 1B',
            'Set 2A',
            'Set 2B',
            'Set 3A',
            'Set 3B',
          ],
        ],
        body: tableData,
        startY: 40, // Position du tableau sous le titre
        headStyles: { fillColor: [128, 0, 32], textColor: [255, 255, 255] }, // Rouge pour le head
      });
  
      // Sauvegarder le PDF
      doc.save(`scores_semaine_${weekNumber}.pdf`);
    });
  };
  


  const handleDownloadScores = (weekMatches) => {
    const csvContent = [
      ['Équipe A', 'Équipe B', 'Set 1', 'Set 2', 'Set 3'],
      ...weekMatches.map((match) =>
        [
          match.teamA,
          match.teamB,
          match.sets[0]?.teamAScore || 0,
          match.sets[1]?.teamAScore || 0,
          match.sets[2]?.teamAScore || 0,
        ].join(',')
      ),
    ].join('\n');
  
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `scores_semaine_${weekMatches[0]?.id || 'n/a'}.csv`;
    link.click();
  };
  
  const validateCode = async () => {
    setErrorMessage('');
    setLoading(true);
    try {
      const res = await fetch('https://us-central1-mendyx-adafe.cloudfunctions.net/validateLeagueCode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
  
      const data = await res.json();
      if (data.valid && data.leagueId) {
        setLeagueId(data.leagueId);
        console.log("✅ Code valide. League ID détecté :", data.leagueId);

      } else {
        setErrorMessage(t('invalidCode'));
      }
    } catch (error) {
      console.error('Erreur de validation :', error);
      setErrorMessage(t('genericError'));
    } finally {
      setLoading(false);
    }
  };
  
 

  useEffect(() => {
    const fetchMatches = async () => {
      if (!leagueId) return;
      setLoading(true);
      try {
        const res = await fetch(`https://us-central1-mendyx-adafe.cloudfunctions.net/getLeagueMatches?leagueId=${leagueId}`);
const { matches: fetchedMatches } = await res.json();

if (!Array.isArray(fetchedMatches)) {
  console.log("📡 Récupération des matchs pour leagueId =", leagueId);

  console.error("La réponse n'est pas un tableau :", fetchedMatches);
  throw new Error("Format de données invalide reçu depuis getLeagueMatches");
}

    
        const formattedMatches = fetchedMatches
          .map((match) => ({
            id: match.id,
            ...match,
            locked: match.locked || false,
            sets: match.sets || [
              { teamAScore: 0, teamBScore: 0 },
              { teamAScore: 0, teamBScore: 0 },
              { teamAScore: 0, teamBScore: 0 },
            ],
          }))
          .sort((a, b) => {
            const dateA = new Date(a.date || '1970-01-01');
            const dateB = new Date(b.date || '1970-01-01');
            return dateA - dateB;
          });
    
        groupMatchesByWeek(formattedMatches);
      } catch (error) {
        console.error('Erreur lors de la récupération des matchs :', error);
      } finally {
        setLoading(false);
      }
    };
    

    fetchMatches();
  }, [leagueId]);

  const groupMatchesByWeek = (matches) => {
    const totalMatches = matches.length;
    const numberOfWeeks = Math.ceil(totalMatches / 2); // 2 matchs par semaine
    const grouped = Array.from({ length: numberOfWeeks }, () => []);
  
    matches.forEach((match, index) => {
      const weekIndex = Math.floor(index / 2);
      grouped[weekIndex].push(match);
    });
  
    // Vérifier si la dernière semaine contient 1 seul match (cas impair)
    if (grouped[numberOfWeeks - 1].length === 1 && totalMatches > 1) {
      grouped[numberOfWeeks - 2].push(grouped[numberOfWeeks - 1][0]);
      grouped.pop(); // Supprime la dernière semaine vide
    }
  
    setGroupedMatches(grouped);
    setCollapsedWeeks(new Array(grouped.length).fill(false));

  };
  
  
  const handleScoreChange = (matchId, setIndex, teamKey, score) => {
    setGroupedMatches((prevGroupedMatches) =>
      prevGroupedMatches.map((weekMatches) =>
        weekMatches.map((match) =>
          match.id === matchId
            ? {
                ...match,
                sets: match.sets.map((set, index) =>
                  index === setIndex ? { ...set, [teamKey]: score } : set
                ),
              }
            : match
        )
      )
    );
  };

  const handleScoreSave = async (match) => {
    setSavingMatchId(match.id);
    try {
      await fetch('https://us-central1-mendyx-adafe.cloudfunctions.net/saveMatchScore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leagueId,
          matchId: match.id,
          sets: match.sets,
        }),
      });
  
      alert(t('matchSaved', { teamA: match.teamA, teamB: match.teamB }));
    } catch (error) {
      console.error("Erreur lors de l'enregistrement :", error);
    } finally {
      setSavingMatchId(null);
    }
  };
  
  if (!leagueId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="p-8 bg-white rounded-lg shadow-md w-full max-w-md text-center">
          <h2 className="text-2xl font-semibold text-center text-[#800020] mb-6">
            {t('enterCode')}
          </h2>
          <input
  type="text"
  className="w-full p-3 border rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-[#800020] text-center"
  placeholder={t('codePlaceholder')}
  value={code}
  onChange={(e) => setCode(e.target.value.trim())} // Supprime les espaces avant/après
  onPaste={(e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').trim(); // Nettoyer les espaces
    setCode(pastedText);
  }}
/>

          {errorMessage && <p className="text-sm text-red-500 mb-4">{errorMessage}</p>}
          <button
            onClick={validateCode}
            className="w-full bg-[#800020] text-white py-3 rounded-lg hover:bg-red-600 transition"
          >
            {loading ? t('validating') : t('validate')}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen text-center">{t('loadingMatches')}</div>;
  }

  return (
    <div className="container mx-auto my-16 px-4 text-center">
      <h2 className="text-3xl font-bold text-center text-[#800020] mb-8">{t('enterScores')}</h2>
      {groupedMatches.length > 0 ? (
  groupedMatches.map((weekMatches, weekIndex) => (
    <div key={`week-${weekIndex}`} className="mb-8 text-center">
      <h2 className="text-2xl font-semibold text-[#800020]">
  {t('week')} {weekIndex + 1} ({groupedMatches.length} {groupedMatches.length > 1 ? t('weeks') : t('oneWeek')})
</h2>

  <div className="flex items-center justify-center gap-4 mb-4">
    <button
      onClick={() => toggleWeekCollapse(weekIndex)}
      className="flex items-center text-[#800020] hover:scale-105 transition-transform"
    >
      <span className="mr-2 text-xl font-semibold">{t('week')} {weekIndex + 1}</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`h-6 w-6 transform transition-transform ${collapsedWeeks[weekIndex] ? 'rotate-180' : ''}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
    <button
  onClick={() => handleDownloadPDF(weekMatches, weekIndex + 1)}
  className="bg-gradient-to-r from-[#800020] to-[#ea4c89] text-white w-10 h-10 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition"
  title={t('downloadScores')}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="white"
  >
    <path
      d="M12 4v12m0 0l-6-6m6 6l6-6"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect x="4" y="19" width="16" height="2" fill="white" />
  </svg>
</button>

  </div>
  {!collapsedWeeks[weekIndex] && (
    <div className="flex flex-wrap justify-center gap-6">
      {weekMatches.map((match) => (
        <MatchScore
          key={match.id}
          match={match}
          onScoreSave={handleScoreSave}
          onScoreChange={handleScoreChange}
          isSaving={savingMatchId === match.id}
          t={t}
        />
      ))}
    </div>
  )}
</div>

        ))
      ) : (
        <p className="text-center text-gray-500">{t('noMatch')}</p>
      )}
    </div>
  );
};

export default EncodeScoresLeague;
